import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email address.";
    if (form.message.trim().length < 10)
      return "Please give us a few more details (10 characters minimum).";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setStatus({ state: "error", error: err });
      return;
    }
    setStatus({ state: "loading", error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e2) {
      const detail =
        e2?.response?.data?.detail?.[0]?.msg ||
        e2?.response?.data?.detail ||
        "Sorry — something went wrong. Please try again.";
      setStatus({ state: "error", error: String(detail) });
    }
  };

  return (
    <>
      <SEO
        title="Contact PeptiChill — UK peptide cooler fridge"
        description="Get in touch with the PeptiChill team. We typically reply within one working day."
        path="/contact"
      />
      <section className="bg-ice-light py-16 lg:py-24" data-testid="page-contact">
        <div className="container-pc grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left column */}
          <div className="lg:col-span-5">
            <p className="eyebrow">Contact</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-navy tracking-tightest leading-[1.04]">
              Get in touch.
            </h1>
            <p className="mt-5 text-lg text-muted leading-relaxed max-w-md">
              We typically reply within one working day.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-3">
                <span className="mt-1 inline-flex w-9 h-9 items-center justify-center rounded-full bg-white border border-silver/60 text-cyan">
                  <Mail size={16} strokeWidth={2} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted">Email</div>
                  <a
                    href={`mailto:${SITE.email}`}
                    className="text-navy font-medium hover:text-cyan transition-colors"
                    data-testid="contact-email-link"
                  >
                    {SITE.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 inline-flex w-9 h-9 items-center justify-center rounded-full bg-white border border-silver/60 text-cyan">
                  <MapPin size={16} strokeWidth={2} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted">Registered address</div>
                  <div className="text-ink/80 text-sm leading-relaxed">
                    {/* [REVIEW – add registered address] */}
                    PeptiChill, c/o Peptigen Labs<br />
                    United Kingdom
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="rounded-2xl bg-white border border-silver/60 shadow-card p-6 lg:p-8"
              data-testid="contact-form"
              noValidate
            >
              {status.state === "success" ? (
                <div
                  className="flex items-start gap-3 rounded-xl bg-ice border border-cyan-soft/60 p-5"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="text-cyan shrink-0 mt-0.5" size={22} />
                  <div>
                    <div className="font-semibold text-navy">Thanks — we&rsquo;ll be in touch.</div>
                    <p className="text-sm text-ink/70 mt-1">
                      Your message has been received. We typically reply within one working day.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    autoComplete="name"
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    autoComplete="email"
                    inputMode="email"
                  />
                  <div className="sm:col-span-2">
                    <Field
                      label="Subject"
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs uppercase tracking-widest text-muted mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={6}
                      required
                      placeholder="How can we help?"
                      data-testid="contact-input-message"
                      className="w-full rounded-xl border border-silver/70 bg-white px-4 py-3 text-sm text-ink placeholder:text-muted/70 focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition resize-y"
                    />
                  </div>

                  {status.state === "error" && (
                    <div
                      className="sm:col-span-2 text-sm text-destructive rounded-xl bg-destructive/5 border border-destructive/20 px-4 py-3"
                      data-testid="contact-error"
                      role="alert"
                    >
                      {status.error}
                    </div>
                  )}

                  <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-xs text-muted">
                      We&rsquo;ll only use this to reply to your enquiry.
                    </p>
                    <button
                      type="submit"
                      disabled={status.state === "loading"}
                      data-testid="contact-submit-button"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white h-12 px-7 text-sm font-semibold tracking-wide hover:brightness-110 transition disabled:opacity-60"
                    >
                      {status.state === "loading" && (
                        <Loader2 size={16} className="animate-spin" />
                      )}
                      {status.state === "loading" ? "Sending…" : "Send message"}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, type = "text", required, value, onChange, autoComplete, inputMode }) {
  return (
    <div>
      <label
        htmlFor={`field-${name}`}
        className="block text-xs uppercase tracking-widest text-muted mb-2"
      >
        {label}
        {required && <span className="text-cyan"> *</span>}
      </label>
      <input
        id={`field-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        data-testid={`contact-input-${name}`}
        className="w-full h-12 sm:h-11 rounded-xl border border-silver/70 bg-white px-4 text-base sm:text-sm text-ink placeholder:text-muted/70 focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition"
      />
    </div>
  );
}
